import React, { useEffect, useState } from 'react';
import * as Filestack from 'filestack-js';
import { v4 as uuid } from 'uuid';

/**
 * Helper hook responsible for creating, opening, and closing
 * the filestack picker component used for file and image uploads.
 *
 * @param {Object} props The hook's props.
 * @param {Object} props.filestack The filestack settings data.
 * @param {String} props.filestack.apiKey The filestack api key.
 * @param {Object} props.filestack.security The filestack security data.
 * @param {Boolean} props.filestack.production True if mode is production.
 * @param {Object} props.filestack.path The filestack path.
 * @param {Object} props.filestack.container The filestack container.
 * @param {Object} props.filestack.region The filestack region.
 * @param {Object} props.options The options for the filestack picker component.
 * @param {Function} props.onSuccess Callback to handle upload successes.
 * @param {Function} props.onError Callback to handle upload errors.
 *
 * @returns {Object} The unique ID for the filestack picker that should be
 * used as an ID for the button responsible for opening the picker and a callback
 * function to toggle the picker open state.
 */
const useFilestackPicker = (props) => {

	const {
		id,
		filestack,
		options,
		onSuccess,
		onError
	} = props;

	// Use an id if provided, otherwise set a default
	const [pickerId] = useState(id || `file-upload-picker-${uuid()}`);

	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {

		if (!filestack || (filestack.production && !filestack.security) || !filestack.apiKey) {
			return undefined;
		}

		// Don't create the picker if it isn't open
		if (!isOpen) { return undefined; }

		const storeSettings = {};

		// Only set store settings in production
		if (filestack.production) {
			storeSettings.location = 's3';
			storeSettings.path = filestack.path;
			storeSettings.container = filestack.container;
			storeSettings.region = filestack.region;
		}

		const clientOptions = {
			security: filestack.security,
		};

		// Initialize the filestack client
		const filestackClient = Filestack.init(filestack.apiKey, clientOptions);

		// Initialize and open the picker
		const pickerOptions = {
			...options,
			container: `#${pickerId}`,
			storeTo: storeSettings,
			onUploadDone: onSuccess,
			onClose: () => setIsOpen(false),
		};

		const filestackPicker = filestackClient.picker(pickerOptions);

		filestackPicker.open().catch(onError);

		return () => { filestackPicker.close(); };


	}, [filestack, isOpen, onError, onSuccess, pickerId, options]);

	return {
		pickerId,
		togglePicker: (prev) => setIsOpen(!prev),
	};
};

export default useFilestackPicker;
